import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};

export default function ServicesPage() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };
    fetchServices();
  }, []);

  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center">
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/28757576/pexels-photo-28757576.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Tour guide services"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1B4D3E]/90 to-[#1B4D3E]/60" />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.p variants={fadeInUp} className="label-style text-white/70 mb-4">
              Our Services
            </motion.p>
            <motion.h1 variants={fadeInUp} className="text-4xl md:text-6xl text-white max-w-2xl">
              Complete Tourism & Business Solutions
            </motion.h1>
          </motion.div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="max-w-3xl mb-16">
              <h2 className="text-3xl md:text-4xl mb-6">
                Everything You Need for Your Indonesian Journey
              </h2>
              <p className="text-[#666666] text-lg leading-relaxed">
                Whether you're seeking adventure, cultural immersion, or business opportunities, 
                our comprehensive services ensure your experience in Indonesia exceeds expectations.
              </p>
            </motion.div>

            <div className="space-y-16">
              {services.map((service, index) => (
                <motion.div
                  key={service.id}
                  variants={fadeInUp}
                  data-testid={`service-detail-${service.id}`}
                  className={`grid md:grid-cols-2 gap-12 items-center ${
                    index % 2 === 1 ? "" : ""
                  }`}
                >
                  <div className={index % 2 === 1 ? "md:order-2" : ""}>
                    <div className="overflow-hidden rounded-lg">
                      <img 
                        src={service.image_url}
                        alt={service.name}
                        className="w-full aspect-video object-cover hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  </div>
                  <div className={index % 2 === 1 ? "md:order-1" : ""}>
                    <h3 className="text-2xl md:text-3xl mb-4">{service.name}</h3>
                    <p className="text-[#666666] leading-relaxed mb-6">
                      {service.description}
                    </p>
                    <div className="space-y-3 mb-8">
                      {service.features.map((feature, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <div className="w-6 h-6 bg-[#1B4D3E] rounded-full flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                          <span className="text-[#1A1A1A]">{feature}</span>
                        </div>
                      ))}
                    </div>
                    <Link 
                      to="/membership"
                      data-testid={`service-cta-${service.id}`}
                      className="inline-flex items-center gap-2 text-[#0E2954] font-semibold link-hover"
                    >
                      Get Started
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl mb-12 text-center">
              Why Choose IndonesiaTourism?
            </motion.h2>
            <motion.div variants={fadeInUp} className="grid md:grid-cols-4 gap-8">
              {[
                { value: "15+", label: "Years of Experience" },
                { value: "100+", label: "Expert Local Guides" },
                { value: "50+", label: "Partner Hotels" },
                { value: "24/7", label: "Customer Support" }
              ].map((stat, index) => (
                <div key={index} className="text-center p-8 border border-[#E5E5E5] rounded-lg">
                  <p className="text-4xl font-['Playfair_Display'] text-[#0E2954] mb-2">
                    {stat.value}
                  </p>
                  <p className="text-[#666666]">{stat.label}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[#D92525]">
        <div className="container-custom text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl text-white mb-6">
              Ready to Plan Your Trip?
            </h2>
            <p className="text-white/80 max-w-2xl mx-auto mb-8">
              Contact us today to discuss your requirements and let us craft the perfect Indonesian experience for you.
            </p>
            <Link 
              to="/membership"
              data-testid="services-page-cta-btn"
              className="inline-block bg-white text-[#D92525] px-8 py-4 rounded-full font-semibold hover:bg-[#FAFAFA] transition-colors"
            >
              Contact Us Now
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
